import React from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Link } from 'react-router-dom'
import {
    Shield,
    TrendingUp,
    Clock,
    Timer,
    AlertTriangle,
    CheckCircle,
    XCircle,
    ExternalLink,
} from 'lucide-react'

import { Typogaphy } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getServiceSLA,
    getServiceDowntimeLogs,
} from '@/modules/Service/services/sla'
import {
    ServiceSLA,
    SLADowntimeLog,
    SLA_TIER_CONFIG,
    SLATier,
    DOWNTIME_CAUSE_LABELS,
    DowntimeCause,
} from '@/modules/Service/types/sla'
import { generatePathDatacentral, paths } from '@/utils/path'
import { formatFromUTC } from '@/utils/function'

dayjs.extend(utc)

interface TicketSLADowntimePreviewProps {
    serviceId: number
    ticketId: number
}

// Format minutes to readable string
const formatMinutes = (minutes: number | undefined) => {
    if (!minutes) return '-'
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

// Get cause badge style - more subtle colors
const getDowntimeCauseBadge = (cause: DowntimeCause) => {
    const styles: Record<DowntimeCause, string> = {
        provider: 'bg-red-50 text-red-600 border-red-100',
        customer: 'bg-gray-50 text-gray-600 border-gray-100',
        third_party: 'bg-purple-50 text-purple-600 border-purple-100',
        force_majeure: 'bg-orange-50 text-orange-600 border-orange-100',
        scheduled: 'bg-green-50 text-green-600 border-green-100',
    }
    return styles[cause] || styles.provider
}

const TicketSLADowntimePreview: React.FC<TicketSLADowntimePreviewProps> = ({
    serviceId,
    ticketId,
}) => {
    const { t } = useTranslation('services')

    // Fetch SLA for this service
    const { data: slaData, isLoading: isLoadingSLA } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, serviceId, 'sla', 'ticket-preview'],
        queryFn: () => getServiceSLA(serviceId),
        enabled: !!serviceId && serviceId > 0,
    })

    // Fetch downtime logs for the service (recent ones)
    const { data: logsData, isLoading: isLoadingLogs } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SLA_DOWNTIME,
            serviceId,
            'ticket-preview',
            ticketId,
        ],
        queryFn: () =>
            getServiceDowntimeLogs(serviceId, {
                page: 1,
                page_size: 10,
                q: '',
            }),
        enabled: !!serviceId && serviceId > 0,
    })

    const sla = slaData?.result as ServiceSLA | undefined
    const logs = logsData?.results || []

    // Filter logs by ticket_id if available, otherwise show recent ones
    const ticketLogs = logs.filter(
        (log: SLADowntimeLog) => log.ticket_id === ticketId
    )
    const hasTicketLogs = ticketLogs.length > 0
    const displayLogs = hasTicketLogs ? ticketLogs : logs.slice(0, 3)

    const hasSLA = !!sla
    // Get tier from template_tier object or sla_template
    const tierObj = sla?.template_tier
    const tier = (
        tierObj?.valid ? tierObj.sla_tier : sla?.sla_template?.template_tier
    ) as SLATier | undefined
    const tierConfig =
        tier && tier !== 'custom'
            ? SLA_TIER_CONFIG[tier as Exclude<SLATier, 'custom'>]
            : null

    // Use effective values from backend (computed from template or manual config)
    const uptime =
        sla?.effective_uptime_guarantee ||
        sla?.uptime_guarantee ||
        tierConfig?.uptime

    // Get response/resolution from effective_sla_rules (first rule or default)
    const defaultRule =
        sla?.effective_sla_rules?.find(
            (r) => r.ticket_type_group === 'default'
        ) || sla?.effective_sla_rules?.[0]
    const responseTime =
        defaultRule?.response_time_minutes ||
        sla?.template_default_response_time ||
        tierConfig?.responseTimeMinutes
    const resolutionTime =
        defaultRule?.resolution_time_minutes ||
        sla?.template_default_resolution_time ||
        tierConfig?.resolutionTimeMinutes

    const isLoading = isLoadingSLA || isLoadingLogs

    if (isLoading) {
        return (
            <Paper className="p-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-5 w-16" />
                </div>
            </Paper>
        )
    }

    // No SLA and no downtime logs - don't show anything
    if (!hasSLA && displayLogs.length === 0) {
        return null
    }

    const slaDetailUrl = `${generatePathDatacentral(paths.serviceDetail, {
        id: serviceId,
    })}?tab=sla`

    // Count active downtimes
    const activeDowntimes = displayLogs.filter(
        (log: SLADowntimeLog) => !log.downtime_end
    ).length

    return (
        <Paper className="p-0 overflow-hidden">
            {/* Header */}
            <div className="px-3 py-2.5 flex items-center justify-between border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <Typogaphy size="small" className="font-medium">
                        Service SLA
                    </Typogaphy>
                    {tier && (
                        <Badge
                            variant="outline"
                            className="text-xs"
                            style={{
                                borderColor: tierConfig?.color,
                                color: tierConfig?.color,
                            }}
                        >
                            {tier.toUpperCase()}
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {activeDowntimes > 0 && (
                        <Badge
                            variant="destructive"
                            className="text-xs animate-pulse"
                        >
                            {activeDowntimes} active
                        </Badge>
                    )}
                    <Link
                        to={slaDetailUrl}
                        target="_blank"
                        className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>

            {/* Content */}
            <div className="p-3 space-y-3">
                {/* SLA Metrics - cleaner style */}
                {hasSLA && (
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-gray-50 rounded border border-gray-100">
                            <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
                                <TrendingUp className="w-3 h-3" />
                                <span className="text-xs">Uptime</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                                {uptime}%
                            </span>
                        </div>
                        <div className="p-2 bg-gray-50 rounded border border-gray-100">
                            <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
                                <Clock className="w-3 h-3" />
                                <span className="text-xs">
                                    {t('sla.response', 'Phản hồi')}
                                </span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                                {formatMinutes(responseTime)}
                            </span>
                        </div>
                        <div className="p-2 bg-gray-50 rounded border border-gray-100">
                            <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
                                <Timer className="w-3 h-3" />
                                <span className="text-xs">
                                    {t('sla.resolution', 'Xử lý')}
                                </span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                                {formatMinutes(resolutionTime)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Downtime Logs */}
                {displayLogs.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-gray-500">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span className="text-xs font-medium">
                                {hasTicketLogs
                                    ? t(
                                          'sla.ticket_downtimes',
                                          'Downtime liên kết với ticket'
                                      )
                                    : t(
                                          'sla.recent_downtimes',
                                          'Downtime gần đây'
                                      )}
                            </span>
                            <Badge
                                variant="secondary"
                                className="text-xs h-4 px-1.5"
                            >
                                {displayLogs.length}
                            </Badge>
                        </div>

                        <div className="space-y-1.5">
                            {displayLogs.map((log: SLADowntimeLog) => {
                                const isOngoing = !log.downtime_end
                                const duration = isOngoing
                                    ? dayjs().diff(
                                          dayjs.utc(log.downtime_start),
                                          'minute'
                                      )
                                    : log.downtime_minutes

                                return (
                                    <div
                                        key={log.downtime_log_id}
                                        className={`p-2 rounded border ${
                                            isOngoing
                                                ? 'bg-red-50 border-red-100'
                                                : 'bg-gray-50 border-gray-100'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                {/* Badges row */}
                                                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                                    {log.downtime_cause && (
                                                        <Badge
                                                            variant="outline"
                                                            className={`text-xs h-5 ${getDowntimeCauseBadge(log.downtime_cause)}`}
                                                        >
                                                            {
                                                                DOWNTIME_CAUSE_LABELS[
                                                                    log
                                                                        .downtime_cause
                                                                ]
                                                            }
                                                        </Badge>
                                                    )}
                                                    {isOngoing && (
                                                        <Badge
                                                            variant="destructive"
                                                            className="text-xs h-5 animate-pulse"
                                                        >
                                                            Đang diễn ra
                                                        </Badge>
                                                    )}
                                                    {log.counts_against_sla ? (
                                                        <span className="flex items-center gap-0.5 text-xs text-red-500">
                                                            <XCircle className="w-3 h-3" />
                                                            Tính SLA
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-0.5 text-xs text-green-500">
                                                            <CheckCircle className="w-3 h-3" />
                                                            Không tính
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Time */}
                                                <p className="text-xs text-gray-500">
                                                    {formatFromUTC(
                                                        log.downtime_start
                                                    )}
                                                    {' → '}
                                                    {isOngoing
                                                        ? 'now'
                                                        : formatFromUTC(
                                                              log.downtime_end
                                                          )}
                                                </p>
                                            </div>
                                            {/* Duration */}
                                            <div
                                                className={`text-sm font-semibold ${isOngoing ? 'text-red-600' : 'text-gray-700'}`}
                                            >
                                                {formatMinutes(duration)}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* No downtime */}
                {displayLogs.length === 0 && hasSLA && (
                    <div className="flex items-center gap-1.5 p-2 bg-green-50 rounded border border-green-100 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs">
                            {t('sla.no_downtime', 'Chưa có downtime')}
                        </span>
                    </div>
                )}
            </div>
        </Paper>
    )
}

export default TicketSLADowntimePreview
